/**
 * Proactive session keepalive — refresh before access-token expiry and on tab focus.
 */

import { getJwtExpiryMs, ACCESS_TOKEN_REFRESH_BUFFER_MS } from '@/lib/jwt-client';
import { getBearerTokenSync } from '@/lib/auth-token';
import { isServerSessionValidated } from '@/lib/auth-session-state';
import {
  detachSessionRefreshBroadcast,
  refreshBrowserSession,
  shouldRunProactiveRefresh,
} from '@/lib/session-refresh';
import { useAuth } from '@/stores/use-auth';

const KEEPALIVE_POLL_MS = 60_000;

function msUntilRefresh(token: string | null): number | null {
  if (!token) {
    return null;
  }
  const expiryMs = getJwtExpiryMs(token);
  if (expiryMs === null) {
    return null;
  }
  const refreshAt = expiryMs - ACCESS_TOKEN_REFRESH_BUFFER_MS;
  return Math.max(0, refreshAt - Date.now());
}

/**
 * Install proactive refresh while authenticated. Returns a cleanup function.
 */
export function startSessionKeepalive(isAuthenticated: boolean): () => void {
  if (typeof window === 'undefined' || !isAuthenticated) {
    return () => undefined;
  }

  let cancelled = false;

  const maybeRefresh = (): void => {
    if (cancelled || !isServerSessionValidated() || !shouldRunProactiveRefresh()) {
      return;
    }
    void refreshBrowserSession({ trigger: 'proactive' });
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      maybeRefresh();
    }
  };

  const intervalId = window.setInterval(() => {
    const token = getBearerTokenSync() ?? useAuth.getState().token;
    const waitMs = msUntilRefresh(token);
    if (waitMs === null) {
      maybeRefresh();
      return;
    }
    if (waitMs > KEEPALIVE_POLL_MS) {
      return;
    }
    maybeRefresh();
  }, KEEPALIVE_POLL_MS);

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', maybeRefresh);
  maybeRefresh();

  return () => {
    cancelled = true;
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', maybeRefresh);
    detachSessionRefreshBroadcast();
  };
}
